import { format, parseISO } from 'date-fns';
import { DATE_FORMAT, DATETIME_FORMAT, TIME_FORMAT } from '../config/constants';

/**
 * Format date to display format
 */
export const formatDate = (date, formatStr = DATE_FORMAT) => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatStr);
  } catch (error) {
    return '';
  }
};

/**
 * Format datetime to display format
 */
export const formatDateTime = (date) => {
  return formatDate(date, DATETIME_FORMAT);
};

/**
 * Format time to display format
 */
export const formatTime = (date) => {
  return formatDate(date, TIME_FORMAT);
};

/**
 * Format currency
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format phone number with country code
 */
export const formatPhoneNumber = (countryCode, phoneNumber) => {
  if (!phoneNumber) return '';
  const ph = String(phoneNumber).trim();
  const cc = String(countryCode || '').trim();
  if (!cc) return ph;

  // If phone already contains the country code (with or without +), don't duplicate
  const phNoSpaces = ph.replace(/\s+/g, '');
  const ccNormalized = cc.replace(/\s+/g, '');
  const ccNoPlus = ccNormalized.replace(/^\+/, '');

  if (
    phNoSpaces.startsWith(ccNormalized) ||
    phNoSpaces.startsWith('+' + ccNoPlus) ||
    phNoSpaces.startsWith(ccNoPlus)
  ) {
    return ph;
  }

  return `${cc} ${ph}`.trim();
};

/**
 * Debounce function
 */
export const debounce = (func, delay = 300) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

/**
 * Generate initials from name
 */
export const getInitials = (name) => {
  if (!name) return '?';
  const clean = String(name).trim();
  if (clean.length === 0) return '?';

  // If it's a number (with optional + or spaces), treat as phone
  const numericOnly = clean.replace(/[+\s-]/g, '');
  if (/^\d+$/.test(numericOnly) && numericOnly.length >= 2) {
    return numericOnly.substring(0, 2);
  }

  // Handle names
  const words = clean.split(/\s+/).filter(w => w.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    const single = words[0];
    // If it's a single word but looks like a number, we already handled it above.
    // Otherwise return first char.
    return single.charAt(0).toUpperCase();
  }
  
  // Return first letter of first word and first letter of last word
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

/**
 * Get avatar color scheme based on name
 */
export const getAvatarColorScheme = (name) => {
  const colorSchemes = [
    { bg: '#f3e8ff', text: '#9333ea' }, // Purple
    { bg: '#ffedd5', text: '#f97316' }, // Orange
    { bg: '#dbeafe', text: '#2563eb' }, // Blue
    { bg: '#fce7f3', text: '#db2777' }, // Pink
    { bg: '#cffafe', text: '#0891b2' }, // Cyan
    { bg: '#dcfce7', text: '#16a34a' }, // Green
    { bg: '#fef3c7', text: '#d97706' }, // Amber
    { bg: '#e0f2fe', text: '#0284c7' }, // Sky
    { bg: '#ede9fe', text: '#7c3aed' }, // Violet
    { bg: '#f5f5f4', text: '#57534e' }, // Stone
  ];
  const stringHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash);
  };
  return colorSchemes[stringHash(name || '') % colorSchemes.length] || colorSchemes[2];
};

/**
 * Check if avatar URL is a generic placeholder
 */
export const isGenericAvatar = (url) => {
  if (!url) return true;
  const genericPatterns = [
    'img_avatar',
    'default-avatar',
    'user.png',
    'placeholder',
    'avatar.png'
  ];
  return genericPatterns.some(pattern => String(url).toLowerCase().includes(pattern));
};


/**
 * Truncate text
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Convert bytes to human readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Download file from blob
 */
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Parse CSV file
 */
export const parseCSV = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const rows = text.split('\n').map(row => row.split(','));
      resolve(rows);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

/**
 * Generate random color
 */
export const generateColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 60%)`;
};

/**
 * Check if value is empty
 */
export const isEmpty = (value) => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' && Object.keys(value).length === 0)
  );
};

/**
 * Deep clone object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Validate email
 */
export const isValidEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validate phone number
 */
export const isValidPhone = (phone) => {
  const regex = /^\d{10,15}$/;
  return regex.test(phone.replace(/\s/g, ''));
};

/**
 * Calculate percentage
 */
export const calculatePercentage = (value, total) => {
  if (!total) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Sort array by key
 */
export const sortByKey = (array, key, order = 'asc') => {
  return [...array].sort((a, b) => {
    if (a[key] < b[key]) return order === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

/**
 * Group array by key
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Get query params from URL
 */
export const getQueryParams = (search) => {
  return Object.fromEntries(new URLSearchParams(search));
};

/**
 * Build query string from object
 */
export const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      query.append(key, value);
    }
  });
  return query.toString();
};

/**
 * Copy to clipboard
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get error message from API response
 */
export const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.message ||
    'An unexpected error occurred'
  );
};

/**
 * Scroll to top
 */
export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Check if user has permission
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  return userPermissions.includes(requiredPermission);
};

/**
 * Check if user has any of the permissions
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has all permissions
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};
